import { Injectable, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { FormGroup, FormArray, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { BaseService } from '../services/data.service';
import { controlHttp, FormButton, FormFieldConfig, FormListConfig } from './models/form.models';

@Injectable()
export class GenericFormHandlerService {
  // Form configuration
  @Input() formFields: FormFieldConfig[] = [];
  @Input() formLists: FormListConfig[] = [];
  @Input() formButtons: FormButton[] = [];

  //varios formularios:
  @Input() formSections: Record<string, FormFieldConfig[]> = {};
  
  // API configuration
  @Input() apiBasePath: string = '';// this.apiBasePath = 'proveedores';//siempre debe ir null a menos que vaya a un curl especifico
  @Input() apiResourcePath: string = '';
  @Input() apiIdentifier: any = null;
  
  // Operation flags
  @Input() currentOperation: controlHttp = controlHttp.CREATE;
  @Input() usePartialUpdate: boolean = false;
  @Input() allowDeletion: boolean = false;
  @Input() closeAfterSave: boolean = true;
  
  // Event emitters
  @Output() onCreateSuccess = new EventEmitter<any>();
  @Output() onUpdateSuccess = new EventEmitter<any>();
  @Output() onDeleteSuccess = new EventEmitter<any>();
  @Output() onFormReady = new EventEmitter<string[]>();
  
  // Form state
  mainForm: FormGroup = new FormGroup({});
  isLoading: boolean = false;
  hasUnsavedChanges: boolean = false;
  initialFormValues: any = null;
  
  // Responsive handling
  currentScreenSize$ = new BehaviorSubject<string>('large');
  private screenWidth: number | undefined;

  constructor(
    public apiService: BaseService,
    private notificationService: ToastrService,
    private dialogReference?: MatDialogRef<any>
  ) {}
  private destroy$ = new Subject<void>();

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Initializes the form service
   * @param loadData Whether to load existing data (for update/read operations)
   */
  async initializeForm(loadData: boolean = true): Promise<void> {
    this.setupResponsiveHandling();
    this.buildFormStructure();
    
    if (this.currentOperation !== controlHttp.CREATE && loadData) {
      await this.fetchFormData();
    }
  }

  private buildFormStructure(): void {
    console.log('Building form structure with:', {
      formFields: this.formFields,
      formSections: this.formSections
    });
  
    const formControls: { [key: string]: FormControl } = {};
  
    // Opción 1: Si se usan formFields directamente
    if (this.formFields && this.formFields.length > 0) {
      console.log('Using flat formFields approach');
      this.formFields.forEach(field => {
        formControls[field.fieldName] = this.createFormControl(field);
      });
    }
    // Opción 2: Si se usan secciones (recomendado)
    else {
      console.log('Using formSections approach');
      Object.values(this.formSections).forEach(section => {
        if (section && section.length > 0) {
          section.forEach(field => {
            formControls[field.fieldName] = this.createFormControl(field);
          });
        }
      });
    }
  
    console.log('Created form controls:', Object.keys(formControls));
    this.mainForm = new FormGroup(formControls);
    this.onFormReady.emit(Object.keys(formControls));
    this.setupChangeDetection();
  }
    // Método auxiliar para crear controles (reutilizable)
    private createFormControl(field: FormFieldConfig): FormControl {
      return new FormControl(
        field.initialValue,
        this.getValidators(field)
      );
    }
  private getValidators(field: FormFieldConfig): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    
    if (field.isRequired) {
      validators.push(Validators.required);
    }
    
    field.validations?.forEach(validation => {
      switch (validation.type) {
        case 'required': validators.push(Validators.required); break;
        case 'min': validators.push(Validators.min(validation.value)); break;
      }
    });
    
    return validators;
  }
  private setupChangeDetection(): void {
    this.initialFormValues = this.mainForm.value;
    this.mainForm.valueChanges.subscribe(currentValues => {
      this.hasUnsavedChanges = Object.keys(this.initialFormValues).some(
        key => currentValues[key] !== this.initialFormValues[key]
      );
    });
  }

  private setupResponsiveHandling(): void {
    this.screenWidth = window.innerWidth;
    this.determineScreenSize(this.screenWidth);
  }

  @HostListener('window:resize', ['$event'])
  private onWindowResize(event: Event): void {
    this.screenWidth = window.innerWidth;
    this.determineScreenSize(this.screenWidth);
  }

  private determineScreenSize(width: number): void {
    if (width < 576) this.currentScreenSize$.next('xsmall');
    else if (width < 768) this.currentScreenSize$.next('small');
    else if (width < 992) this.currentScreenSize$.next('medium');
    else if (width < 1200) this.currentScreenSize$.next('large');
    else this.currentScreenSize$.next('xlarge');
  }

  async fetchFormData(): Promise<void> {
    this.isLoading = true;
    this.mainForm.disable();

    try {
      const apiUrl = this.buildApiUrl();
      console.log("fetchFormData en GenericCrud")
      const response = await this.apiService.get(apiUrl).toPromise();
      
      this.populateForm(response);
      this.handleIncludedLists(response);
      
      this.isLoading = false;
      return response;
    } catch (error) {
      this.handleDataLoadError(error);
      throw error;
    }
  }

  private buildApiUrl(): string {
    if (this.apiResourcePath && this.apiBasePath) {
      return `${this.apiBasePath}/${this.apiResourcePath}`;
    }else if(this.apiResourcePath && !this.apiBasePath){
      return `${this.apiResourcePath}`;
    }else if(!this.apiResourcePath && this.apiBasePath){
      return `${this.apiBasePath}`;
    }else{
      return `${this.apiIdentifier}`;
    }
  }

  private populateForm(data: any): void {
    this.mainForm.patchValue(data);
    this.initialFormValues = this.mainForm.value;
    this.mainForm.enable();
  }

  private handleIncludedLists(response: any): void {
    if (!response.lists) return;

    this.formLists.forEach(listConfig => {
      const listData = response.lists[listConfig.listName];
      if (listData) {
        const formArray = this.mainForm.get(listConfig.listName) as FormArray;
        listData.forEach((item : any) => {
          const group = this.createListFormGroup(listConfig.fields);
          group.patchValue(item);
          formArray.push(group);
        });
      }
    });
  }

  private createListFormGroup(fields: FormFieldConfig[]): FormGroup {
    const group: { [key: string]: FormControl } = {};
    fields.forEach(field => {
      group[field.fieldName] = new FormControl(field.initialValue);
    });
    return new FormGroup(group);
  }

  private handleDataLoadError(error: any): void {
    this.isLoading = false;
    const errorMessage = this.extractErrorMessage(error);
    this.notificationService.error(
      errorMessage || 'Error loading data. Please try again or contact support if the problem persists.',
      'Load Error'
    );
    this.mainForm.disable();
  }

  /**
   * Extrae el mensaje de error del backend
   * @param error El objeto de error de la petición HTTP
   * @returns El mensaje de error o null si no está disponible
   */
  protected extractErrorMessage(error: any): string | null {
    if (!error) return null;
    
    // Si es HttpErrorResponse, el error está en error.error
    if (error.error) {
      // Si error.error es un string, retornarlo directamente
      if (typeof error.error === 'string') {
        return error.error;
      }
      // Si error.error tiene una propiedad message, usarla
      if (error.error.message) {
        return error.error.message;
      }
      // Si error.error es un objeto con otras propiedades, intentar extraer el mensaje
      if (typeof error.error === 'object') {
        return error.error.message || null;
      }
    }
    
    // Si el error tiene un mensaje directo
    if (error.message) {
      return error.message;
    }
    
    return null;
  }

  async submitForm(formData: any): Promise<any> {
    if (this.mainForm.invalid) {
      this.markFormGroupTouched(this.mainForm);
      console.warn('Por favor complete todos los campos requeridos')
      this.notificationService.info('Por favor complete todos los campos requeridos');
      return false;
    }
  
    this.isLoading = true;
    this.mainForm.disable();
    console.log('this.currentOperation', this.currentOperation)
    try {
      let response;
      switch (this.currentOperation) {
        case controlHttp.CREATE:
          response = await this.createRecord(formData);
          break;
        case controlHttp.UPDATE:
          response = this.usePartialUpdate 
            ? await this.partialUpdateRecord(formData) 
            : await this.fullUpdateRecord(formData);
          break;
        default:
          response = null;
      }
      return response;
    } finally {
      this.isLoading = false;
      this.mainForm.enable();
    }
  }

  private async createRecord(frmValue: any, id = null): Promise<any> {
    const formData = this.mainForm.value;
    try {
      const response = await this.apiService.post(frmValue, this.apiResourcePath).toPromise();
      this.onCreateSuccess.emit(response);
      this.notificationService.success('Record created successfully');
      
      // For subsequent updates if needed
      if (response.id) {
        this.apiIdentifier = response.id;
        this.currentOperation = controlHttp.UPDATE;
      }
      if(this.closeAfterSave){
        this.handleSuccess(response);
      }
      
      
      return response;
    } catch (error) {
      this.handleCreateError(error);
      throw error;
    }
  }

  private handleCreateError(error: any): void {
    const errorMessage = this.extractErrorMessage(error);
    if (error.status === 409) {
      this.notificationService.warning(errorMessage || 'This record already exists or conflicts with existing data.');
    } else {
      this.notificationService.error(errorMessage || 'Failed to create record. Please try again.');
    }
  }

  private async fullUpdateRecord(frmValue: any, id = null): Promise<any> {
    const formData = this.mainForm.value;
    try {
      const response = await this.apiService.put(formData, this.buildApiUrl()).toPromise();
      this.onUpdateSuccess.emit(response);
      console.log('Record updated successfully')
      this.handleSuccess(response);
      this.notificationService.success('Record updated successfully');
      return response;
    } catch (error) {
      this.handleUpdateError(error);
      throw error;
    }
  }
    protected handleSuccess(response: any): void {
      if (this.dialogReference) {
        this.dialogReference.close(true); 
      }
    }

  private async partialUpdateRecord(frmValue: any, id = null): Promise<any> {
    const changedValues = this.getChangedValues();
    try {
      const response = await this.apiService.patch(changedValues, this.buildApiUrl()).toPromise();
      this.onUpdateSuccess.emit(response);
      this.notificationService.success('Record updated successfully');
      this.handleSuccess(response);
      return response;
    } catch (error) {
      this.handleUpdateError(error);
      throw error;
    }
  }

  private getChangedValues(): any {
    const changes: { [key: string]: any } = {};
    Object.keys(this.mainForm.controls).forEach(key => {
      const control = this.mainForm.get(key);
      if (control && control.value !== this.initialFormValues[key]) {
        changes[key] = control.value;
      }
    });
    return changes;
  }

  private handleUpdateError(error: any): void {
    const errorMessage = this.extractErrorMessage(error);
    if (error.status === 409) {
      this.notificationService.warning(errorMessage || 'This record cannot be updated due to a conflict.');
    } else {
      this.notificationService.error(errorMessage || 'Failed to update record. Please try again.');
    }
  }

  private async deleteRecord(frmValue: any, id = null): Promise<any> {
    try {
      const response = await this.apiService.delete(this.buildApiUrl()).toPromise();
      this.onDeleteSuccess.emit(response);
      this.notificationService.success('Record deleted successfully');
      
      if (this.dialogReference) {
        this.dialogReference.close(response);
      }
      
      return response;
    } catch (error) {
      this.handleDeleteError(error);
      throw error;
    }
  }

  private handleDeleteError(error: any): void {
    const errorMessage = this.extractErrorMessage(error);
    if (error.status === 409) {
      this.notificationService.warning(errorMessage || 'This record cannot be deleted because it is referenced by other data.');
    } else {
      this.notificationService.error(errorMessage || 'Failed to delete record. Please try again.');
    }
  }

  // Utility methods for form lists
  addListItem(listName: string, initialData: any = {}): void {
    const listArray = this.mainForm.get(listName) as FormArray;
    const listConfig = this.formLists.find(l => l.listName === listName);
    
    if (listConfig) {
      const newGroup = this.createListFormGroup(listConfig.fields);
      newGroup.patchValue(initialData);
      listArray.push(newGroup);
    }
  }
  async onSubmit(): Promise<void> {
    try {
      const result = await this.submitForm(this.mainForm.value);
      if (result) {
        this.dialogReference?.close(true); // Cerrar diálogo con éxito
      }
    } catch (error) {
      const errorMessage = this.extractErrorMessage(error);
      this.notificationService.error(errorMessage || 'Error al guardar los cambios');
    }
  }

  removeListItem(listName: string, index: number): void {
    const listArray = this.mainForm.get(listName) as FormArray;
    listArray.removeAt(index);
  }

  getListValues(listName: string): any[] {
    const listArray = this.mainForm.get(listName) as FormArray;
    return listArray.controls.map(control => control.value);
  }
  protected markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
  
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}



